import React, { useState } from "react";
import { toast } from "react-toastify";
import useAxiosPrivate from "../../common/useAxiosPrivate";
import { ALL_ROLES } from "../../constants/roles";

interface FormState {
  username: string;
  password: string;
  confirmPassword: string;
  name: string;
  email: string;
  roles: string[];
}

const initialState: FormState = {
  username: "",
  password: "",
  confirmPassword: "",
  name: "",
  email: "",
  roles: [],
};

const CreateUserPage: React.FC = () => {
  const axiosPrivate = useAxiosPrivate();
  const [formState, setFormState] = useState<FormState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = (field: keyof FormState, value: string) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const toggleRole = (role: string) => {
    setFormState((prev) => {
      const alreadySelected = prev.roles.includes(role);
      return {
        ...prev,
        roles: alreadySelected
          ? prev.roles.filter((item) => item !== role)
          : [...prev.roles, role],
      };
    });
  };

  const extractErrorMessage = (detail: any): string => {
    if (!detail) {
      return "Failed to create user. Please try again.";
    }

    if (typeof detail === "string") {
      return detail;
    }

    if (Array.isArray(detail)) {
      const messages = detail
        .map((item) => item?.msg || item?.message)
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.join(" \n");
      }
    }

    return "Failed to create user. Please check the input and try again.";
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formState.username.trim()) {
      toast.error("Username is required");
      return;
    }

    if (formState.password !== formState.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    try {
      await axiosPrivate.post("/admin/users", {
        username: formState.username.trim(),
        password: formState.password,
        name: formState.name.trim() || undefined,
        email: formState.email.trim() || undefined,
        roles: formState.roles,
      });
      toast.success("User created successfully");
      setFormState(initialState);
    } catch (error: any) {
      const detail = error?.response?.data?.detail;
      const message = extractErrorMessage(detail);
      if (
        typeof message === "string" &&
        message.includes("Password must be at least 8 characters")
      ) {
        toast.warn(message);
      } else {
        toast.error(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container py-4">
      <h2 className="mb-3">Create User</h2>
      <p className="text-muted mb-4">
        Provide a username, temporary password, and assign the roles the new user
        should have.
      </p>
      <form className="row g-3" onSubmit={handleSubmit}>
        <div className="col-md-6">
          <label className="form-label">Username</label>
          <input
            type="text"
            className="form-control"
            value={formState.username}
            onChange={(event) => updateField("username", event.target.value)}
            placeholder="jane.doe"
            required
            disabled={isSubmitting}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Display Name (optional)</label>
          <input
            type="text"
            className="form-control"
            value={formState.name}
            onChange={(event) => updateField("name", event.target.value)}
            placeholder="Jane Doe"
            disabled={isSubmitting}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Password</label>
          <input
            type="password"
            className="form-control"
            value={formState.password}
            onChange={(event) => updateField("password", event.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Confirm Password</label>
          <input
            type="password"
            className="form-control"
            value={formState.confirmPassword}
            onChange={(event) =>
              updateField("confirmPassword", event.target.value)
            }
            required
            disabled={isSubmitting}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Email (optional)</label>
          <input
            type="email"
            className="form-control"
            value={formState.email}
            onChange={(event) => updateField("email", event.target.value)}
            placeholder="jane.doe@example.com"
            disabled={isSubmitting}
          />
        </div>
        <div className="col-12">
          <label className="form-label">Roles</label>
          <div className="d-flex flex-wrap gap-3">
            {ALL_ROLES.map((role) => (
              <div key={role} className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`role-${role}`}
                  checked={formState.roles.includes(role)}
                  onChange={() => toggleRole(role)}
                  disabled={isSubmitting}
                />
                <label className="form-check-label" htmlFor={`role-${role}`}>
                  {role}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="col-12">
          <button className="btn blue-btn" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Creating..." : "Create User"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateUserPage;
